import { useEffect, useState } from 'react';
import { api } from '../../shared/api/client';
import type { BibleBook } from '../../shared/api/types';
import { useAsync } from '../../shared/ui/useAsync';
import { CitationChooser } from '../classrooms/VersePicker';
import { bibleBooks } from './ScriptureText';

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function distance(a: string, b: string): number {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array<number>(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0]![j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      d[i]![j] = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
      // Letras volteadas («Jaun») cuentan como un solo error.
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i]![j] = Math.min(d[i]![j]!, d[i - 2]![j - 2]! + 1);
    }
  }
  return d[a.length]![b.length]!;
}

/** «Jaun 3:16» → «Juan 3:16»: el libro más parecido (si se parece lo suficiente) con los mismos números. */
export function suggestCitation(text: string, books: readonly BibleBook[]): string | null {
  const m = /^\s*((?:[1-3]\s*)?[\p{L}\s.]+?)\s*(\d[\d\s:.,\-–]*)$/u.exec(text);
  if (!m) return null;
  const word = fold(m[1]!.replace(/\s+/g, ''));
  let best: { book: BibleBook; d: number } | null = null;
  for (const b of books) {
    const name = fold(b.name.replace(/\s+/g, ''));
    const d = Math.min(distance(word, name), word.length >= 3 && name.startsWith(word) ? 0 : 99);
    if (!best || d < best.d) best = { book: b, d };
  }
  if (!best || best.d > Math.max(1, Math.floor(best.book.name.length / 4))) return null;
  const nums = m[2]!.trim().replace(/\s*[:.,]\s*/, ':').replace(/\s+/, ':');
  return `${best.book.name} ${nums}`;
}

/**
 * Campo de cita bíblica a prueba de errores: se revisa mientras se escribe (muestra cómo quedará
 * o sugiere «¿Quisiste decir…?») y también se puede elegir con libro → capítulo → versículos.
 */
export function VerseField({ name, label, defaultValue, placeholder, idPrefix = 'vf' }: { name: string; label: string; defaultValue?: string; placeholder?: string; idPrefix?: string }) {
  const [value, setValue] = useState(defaultValue ?? '');
  const [check, setCheck] = useState<{ ok: true; label: string } | { ok: false; suggestion: string | null } | null>(null);
  const [picking, setPicking] = useState(false);
  const books = useAsync(() => bibleBooks(), []);

  useEffect(() => {
    const text = value.trim();
    setCheck(null);
    if (!text) return;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      api
        .get<{ label: string }>(`/bible/passage?ref=${encodeURIComponent(text)}`, ctrl.signal)
        .then((p) => setCheck({ ok: true, label: p.label }))
        .catch(() => {
          if (!ctrl.signal.aborted) setCheck({ ok: false, suggestion: books.status === 'ready' ? suggestCitation(text, books.data) : null });
        });
    }, 350);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [value, books.status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="field">
      <label htmlFor={`${idPrefix}-input`}>{label}</label>
      <div className="verse-picker__typed">
        <input id={`${idPrefix}-input`} name={name} value={value} maxLength={60} placeholder={placeholder} autoComplete="off" aria-invalid={check && !check.ok ? true : undefined} onChange={(e) => setValue(e.target.value)} />
        <button type="button" className="btn btn--ghost" onClick={() => setPicking((v) => !v)}>
          {picking ? 'Cerrar' : 'Elegir'}
        </button>
      </div>
      {check?.ok ? (
        <p className="small verse-field__ok">✓ {check.label}</p>
      ) : check && !check.ok ? (
        <p className="small text-danger" role="alert">
          No reconozco esa cita.{' '}
          {check.suggestion ? (
            <button type="button" className="scripture-link" onClick={() => setValue(check.suggestion!)}>
              ¿Quisiste decir {check.suggestion}?
            </button>
          ) : (
            'Escríbela como «Juan 3:16» o tócale «Elegir».'
          )}
        </p>
      ) : null}
      {picking ? (
        <CitationChooser
          idPrefix={`${idPrefix}-pick`}
          action="Usar"
          onAdd={(c) => {
            setValue(c);
            setPicking(false);
          }}
        />
      ) : null}
    </div>
  );
}
